import React, { useEffect, useMemo, useRef, useState } from "react";
import { MenuHeader } from "../Dashboard/block/MenuHeader/MenuHeader";
import { MobileMenu } from "../Dashboard/MobileMenu";
import { useActiveCompanyIdApi } from "../../api/useActiveCompanyIdApi";
import { useUnsavedChanges, useAppShell } from "../../layout/AppShellContext";
import { apiCustom } from "../../api/client";
import { errorMessage } from "../../api/errorMessage";
import { PromoAnalyticsResponse, PromoUsageSummary, PromoUsage, usageCount } from "./PromoUsage";
import { formatDate, toDateInputValue } from "../shared/format";
import "../shared/mobileSection.scss";
import "./promoUsage.scss";

type Promo = {
    id: number;
    title: string;
    type: string;
    sale: number;
    registration: boolean;
    from: string | null;
    to: string | null;
    isGlobal: boolean;
    isReusable: boolean;
    forNewUsersOnly: boolean;
    isCollaboration: boolean;
    usage?: PromoUsageSummary;
};

type ServiceTree = Record<string, Array<{ id: number; title: string }>>;

type PromoMetaResponse = {
    company?: {
        title?: string | null;
        address?: string | null;
    };
    serviceTree?: ServiceTree;
};

type PromoListResponse = {
    stats?: {
        total: number;
        active: number;
        upcoming: number;
        expired: number;
        global: number;
        local: number;
    };
    items?: Promo[];
};

type PromoShowResponse = {
    promo?: Promo;
    selectedServices?: number[];
    canEdit?: boolean;
    serviceTree?: ServiceTree;
};

type PromoForm = {
    title: string;
    discountType: "percentage" | "rub";
    discount: string;
    startDate: string;
    endDate: string;
    registration: boolean;
    isReusable: boolean;
    forNewUsersOnly: boolean;
    selectedServices: number[];
};

function createDefaultForm(): PromoForm {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());

    return {
        title: "",
        discountType: "percentage",
        discount: "",
        startDate: toDateInputValue(now),
        endDate: toDateInputValue(nextMonth),
        registration: false,
        isReusable: false,
        forNewUsersOnly: false,
        selectedServices: [],
    };
}

export const PromoCodesPage: React.FC = () => {
    const { value: activeId, resolved } = useActiveCompanyIdApi(); const shell = useAppShell();
    const generation = useRef(0); const detailRequest = useRef(0);
    const analyticsRequest = useRef(0); const analyticsPanel = useRef<HTMLDivElement>(null);
    const [baseline, setBaseline] = useState<PromoForm>(createDefaultForm);
    const [menuOpen, setMenuOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [companyTitle, setCompanyTitle] = useState("");
    const [companyAddress, setCompanyAddress] = useState("");
    const [serviceTree, setServiceTree] = useState<ServiceTree>({});
    const [stats, setStats] = useState<PromoListResponse["stats"]>();
    const [items, setItems] = useState<Promo[]>([]);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [canEdit, setCanEdit] = useState(true);
    const [analytics, setAnalytics] = useState<PromoAnalyticsResponse | null>(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [analyticsError, setAnalyticsError] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("all");
    const [scope, setScope] = useState("all");
    const [form, setForm] = useState<PromoForm>(createDefaultForm);

    useUnsavedChanges(saving || (canEdit && JSON.stringify(form) !== JSON.stringify(baseline)), "Промокод");
    const serviceGroups = useMemo(() => Object.entries(serviceTree), [serviceTree]);

    const loadList = async (nextFilters?: { search?: string; status?: string; scope?: string }) => {
        const epoch = generation.current;
        const currentSearch = nextFilters?.search ?? search;
        const currentStatus = nextFilters?.status ?? status;
        const currentScope = nextFilters?.scope ?? scope;

        const response = await apiCustom.get<PromoListResponse>("/lkfr/promocodes", {
            search: currentSearch,
            status: currentStatus,
            scope: currentScope,
        }, { headers: { "X-Company-Id": activeId }, timeout: 25000 });

        if (epoch !== generation.current) return;
        setStats(response?.stats);
        setItems(response?.items ?? []);
    };

    useEffect(() => {
        let alive = true; const counter = generation; const detailCounter = detailRequest; ++counter.current; ++detailCounter.current;
        ++analyticsRequest.current; setAnalyticsLoading(false); setAnalyticsError(null);
        setLoading(true); setSaving(false); setError(null); setSuccess(null); setItems([]); setStats(undefined); setEditingId(null); setAnalytics(null); setForm(createDefaultForm()); setBaseline(createDefaultForm()); setCanEdit(true); setDetailsLoading(false); setCompanyTitle(""); setCompanyAddress("");
        if (!resolved || !activeId) { setLoading(!resolved); return () => { alive = false; }; }

        Promise.all([
            apiCustom.get<PromoMetaResponse>("/lkfr/promocodes/meta", undefined, { headers: { "X-Company-Id": activeId }, timeout: 25000 }),
            apiCustom.get<PromoListResponse>("/lkfr/promocodes", { search, status, scope }, { headers: { "X-Company-Id": activeId }, timeout: 25000 }),
        ])
            .then(([metaResponse, listResponse]) => {
                if (!alive) {
                    return;
                }

                setCompanyTitle(metaResponse?.company?.title ?? "");
                setCompanyAddress(metaResponse?.company?.address ?? "");
                setServiceTree(metaResponse?.serviceTree ?? {});
                setStats(listResponse?.stats);
                setItems(listResponse?.items ?? []);
            })
            .catch((requestError: any) => {
                if (alive) {
                    setError(requestError?.message ?? "Не удалось загрузить промокоды");
                }
            })
            .finally(() => {
                if (alive) {
                    setLoading(false);
                }
            });

        return () => {
            alive = false; ++counter.current; ++detailCounter.current;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeId, resolved]);

    const resetForm = () => {
        ++detailRequest.current; setDetailsLoading(false);
        ++analyticsRequest.current; setAnalyticsLoading(false); setAnalyticsError(null);
        setEditingId(null);
        setCanEdit(true);
        setAnalytics(null);
        setForm(createDefaultForm()); setBaseline(createDefaultForm());
        setSuccess(null);
        setError(null);
    };

    const toggleService = (serviceId: number) => {
        setForm((current) => ({
            ...current,
            selectedServices: current.selectedServices.includes(serviceId)
                ? current.selectedServices.filter((item) => item !== serviceId)
                : [...current.selectedServices, serviceId],
        }));
    };

    const openPromo = async (promoId: number) => {
        const epoch = generation.current; const requestId = ++detailRequest.current;
        const analyticsId = ++analyticsRequest.current;
        try {
            setDetailsLoading(true);
            setError(null);
            setSuccess(null);
            setAnalytics(null); setAnalyticsError(null); setAnalyticsLoading(true);

            const config = { headers: { "X-Company-Id": activeId }, timeout: 25000 };
            const [promoResult, analyticsResult] = await Promise.allSettled([
                apiCustom.get<PromoShowResponse>(`/lkfr/promocodes/${promoId}`, undefined, config),
                apiCustom.get<PromoAnalyticsResponse>(`/lkfr/promocodes/${promoId}/analytics`, undefined, config),
            ]);

            if (epoch !== generation.current || requestId !== detailRequest.current) return;
            if (promoResult.status === "rejected") throw promoResult.reason;
            const promoResponse = promoResult.value;
            const promo = promoResponse?.promo;
            if (!promo) {
                throw new Error("Промокод не найден");
            }

            setEditingId(promo.id);
            setCanEdit(Boolean(promoResponse?.canEdit));
            if (promoResponse?.serviceTree) {
                setServiceTree(promoResponse.serviceTree);
            }
            if (analyticsId === analyticsRequest.current) {
                if (analyticsResult.status === "fulfilled") {
                    const data = analyticsResult.value;
                    setAnalytics(data ?? null);
                    setItems(current => current.map(item => item.id === promoId && data?.overall ? { ...item, usage: data.overall } : item));
                }
                else setAnalyticsError(errorMessage(analyticsResult.reason, "Не удалось загрузить аналитику. Попробуйте обновить."));
            }
            const next: PromoForm = {
                title: promo.title ?? "",
                discountType: promo.type === "rub" ? "rub" : "percentage",
                discount: String(promo.sale ?? ""),
                startDate: promo.from ?? createDefaultForm().startDate,
                endDate: promo.to ?? createDefaultForm().endDate,
                registration: Boolean(promo.registration),
                isReusable: Boolean(promo.isReusable),
                forNewUsersOnly: Boolean(promo.forNewUsersOnly),
                selectedServices: promoResponse?.selectedServices ?? [],
            }; setForm(next); setBaseline(next);
            requestAnimationFrame(() => { if (epoch === generation.current && requestId === detailRequest.current) analyticsPanel.current?.scrollIntoView({ behavior: "smooth", block: "start" }); });
        } catch (requestError: any) {
            if (epoch !== generation.current || requestId !== detailRequest.current) return;
            setError(errorMessage(requestError, "Не удалось открыть промокод"));
        } finally {
            if (epoch === generation.current && requestId === detailRequest.current) { setDetailsLoading(false); setAnalyticsLoading(false); }
        }
    };

    const refreshAnalytics = async () => {
        if (!editingId || analyticsLoading) return;
        const epoch = generation.current; const requestId = ++analyticsRequest.current;
        setAnalyticsLoading(true); setAnalyticsError(null); setAnalytics(null);
        try {
            const data = await apiCustom.get<PromoAnalyticsResponse>(`/lkfr/promocodes/${editingId}/analytics`, undefined, { headers: { "X-Company-Id": activeId }, timeout: 25000 });
            if (epoch !== generation.current || requestId !== analyticsRequest.current) return;
            setAnalytics(data);
            setItems(current => current.map(promo => promo.id === editingId && data.overall ? { ...promo, usage: data.overall } : promo));
        } catch (err) {
            if (epoch === generation.current && requestId === analyticsRequest.current) setAnalyticsError(errorMessage(err, "Не удалось загрузить аналитику. Попробуйте обновить."));
        } finally {
            if (epoch === generation.current && requestId === analyticsRequest.current) setAnalyticsLoading(false);
        }
    };

    const handleSave = async (event: React.FormEvent) => {
        event.preventDefault();
        if (saving || !canEdit) return;
        const epoch = generation.current;
        try {
            setSaving(true);
            setError(null);
            setSuccess(null);

            const payload = {
                promoCode: form.title,
                discountType: form.discountType,
                discount: Number(form.discount),
                startDate: form.startDate,
                endDate: form.endDate,
                registration: form.registration,
                isReusable: form.isReusable,
                forNewUsersOnly: form.forNewUsersOnly,
                selectedServices: form.selectedServices,
            };

            if (editingId) {
                await apiCustom.put(`/lkfr/promocodes/${editingId}`, payload);
                if (epoch !== generation.current) return;
                setSuccess("Промокод обновлен");
            } else {
                const response = await apiCustom.post<{ promo?: Promo }>("/lkfr/promocodes", payload);
                if (epoch !== generation.current) return;
                setSuccess("Промокод создан");
                if (response?.promo?.id) {
                    setEditingId(response.promo.id);
                }
            }

            setBaseline(form);
            await loadList();
        } catch (requestError: any) {
            if (epoch !== generation.current) return;
            setError(requestError?.message ?? "Не удалось сохранить промокод");
        } finally {
            if (epoch === generation.current) setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!editingId || !canEdit || saving) {
            return;
        }

        const epoch = generation.current;
        const confirmed = window.confirm("Удалить этот промокод?");
        if (!confirmed) {
            return;
        }

        try {
            setSaving(true);
            setError(null);
            setSuccess(null);

            await apiCustom.delete(`/lkfr/promocodes/${editingId}`);
            if (epoch !== generation.current) return;
            resetForm();
            setSuccess("Промокод удален");
            await loadList();
        } catch (requestError: any) {
            if (epoch !== generation.current) return;
            setError(requestError?.message ?? "Не удалось удалить промокод");
        } finally {
            if (epoch === generation.current) setSaving(false);
        }
    };

    const onFiltersChange = async (nextSearch: string, nextStatus: string, nextScope: string) => {
        try {
            setLoading(true);
            setError(null);
            setSearch(nextSearch);
            setStatus(nextStatus);
            setScope(nextScope);
            await loadList({ search: nextSearch, status: nextStatus, scope: nextScope });
        } catch (requestError: any) {
            setError(requestError?.message ?? "Не удалось обновить список");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="section-page promo-codes-page">
            <MenuHeader onMenuClick={() => setMenuOpen(true)} />
            <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />

            <h1 className="section-title">Промокоды</h1>

            <div className="section-card">
                <div className="section-subtitle">{companyTitle || "Текущая точка"}</div>
                <div className="section-helper">{companyAddress || "Адрес не указан"}</div>
            </div>

            {error ? <div className="section-error">{error}</div> : null}
            {success ? <div className="section-success">{success}</div> : null}
            {loading ? <div className="section-empty">Загрузка промокодов...</div> : null}

            {!loading ? (
                <>
                    <div className="section-card">
                        <div className="section-subtitle">Фильтры</div>
                        <div className="section-form">
                            <div className="section-field">
                                <div className="section-label">Поиск</div>
                                <input
                                    className="section-input"
                                    value={search}
                                    onChange={(event) => void onFiltersChange(event.target.value, status, scope)}
                                    placeholder="Введите код или часть названия"
                                />
                            </div>
                            <div className="section-inline">
                                <div className="section-field">
                                    <div className="section-label">Статус</div>
                                    <select
                                        className="section-select"
                                        value={status}
                                        onChange={(event) => void onFiltersChange(search, event.target.value, scope)}
                                    >
                                        <option value="all">Все</option>
                                        <option value="active">Активные</option>
                                        <option value="upcoming">Будущие</option>
                                        <option value="expired">Завершенные</option>
                                    </select>
                                </div>
                                <div className="section-field">
                                    <div className="section-label">Область</div>
                                    <select
                                        className="section-select"
                                        value={scope}
                                        onChange={(event) => void onFiltersChange(search, status, event.target.value)}
                                    >
                                        <option value="all">Все</option>
                                        <option value="local">Локальные</option>
                                        <option value="global">Глобальные</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    {stats ? (
                        <div className="section-card promo-stats">
                            <div className="section-subtitle">Статистика</div>
                            <div className="section-grid">
                                <div className="section-stat">
                                    <div className="section-stat-label">Всего</div>
                                    <div className="section-stat-value">{stats.total}</div>
                                </div>
                                <div className="section-stat">
                                    <div className="section-stat-label">Активные</div>
                                    <div className="section-stat-value">{stats.active}</div>
                                </div>
                                <div className="section-stat">
                                    <div className="section-stat-label">Будущие</div>
                                    <div className="section-stat-value">{stats.upcoming}</div>
                                </div>
                                <div className="section-stat">
                                    <div className="section-stat-label">Завершенные</div>
                                    <div className="section-stat-value">{stats.expired}</div>
                                </div>
                                <div className="section-stat">
                                    <div className="section-stat-label">Локальные</div>
                                    <div className="section-stat-value">{stats.local}</div>
                                </div>
                                <div className="section-stat">
                                    <div className="section-stat-label">Глобальные</div>
                                    <div className="section-stat-value">{stats.global}</div>
                                </div>
                            </div>
                        </div>
                    ) : null}

                    <div className="section-card">
                        <div className="section-item-head">
                            <div className="section-subtitle">Список промокодов</div>
                            <button className="section-btn-secondary" type="button" onClick={() => { if (shell) shell.runGuarded(resetForm); else resetForm(); }}>
                                Новый
                            </button>
                        </div>
                        {items.length === 0 ? (
                            <div className="section-empty">Подходящих промокодов нет</div>
                        ) : (
                            <div className="section-list">
                                {items.map((promo) => {
                                    const isActive = editingId === promo.id;
                                    const isExpired = Boolean(promo.to && new Date(promo.to) < new Date());

                                    return (
                                        <button
                                            key={promo.id}
                                            type="button"
                                            className={`section-item ${isActive ? "active" : ""}`}
                                            onClick={() => { const action = () => { void openPromo(promo.id); }; if(shell)shell.runGuarded(action);else action(); }}
                                        >
                                            <div className="section-item-head">
                                                <div>
                                                    <div className="section-item-title">{promo.title}</div>
                                                    <div className="section-item-meta">
                                                        {promo.type === "rub" ? `${promo.sale} ₽` : `${promo.sale}%`} · {formatDate(promo.from)} - {formatDate(promo.to)}
                                                    </div>
                                                </div>
                                                <div className={`section-badge ${isExpired ? "warning" : "success"}`}>
                                                    {promo.isGlobal ? "Глобальный" : "Локальный"}
                                                </div>
                                            </div>
                                            <div className="section-item-text">
                                                {promo.isReusable ? "Многоразовый" : "Одноразовый"}
                                                {promo.forNewUsersOnly ? " · Только для новых" : ""}
                                                {promo.registration ? " · Только с регистрацией" : ""}
                                            </div>
                                            <div className="promo-row-usage">
                                                <span>Применений: <strong>{usageCount(promo.usage?.usageCount)}</strong></span>
                                                <span>За 30 дней: <strong>{usageCount(promo.usage?.last30Days)}</strong></span>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {editingId && !detailsLoading ? <div ref={analyticsPanel} style={{ scrollMarginTop: 86 }}>
                        <PromoUsage title={form.title} data={analytics} loading={analyticsLoading} error={analyticsError} onRetry={() => void refreshAnalytics()} />
                    </div> : null}

                    <div className="section-card">
                        <div className="section-subtitle">
                            {editingId ? `Редактирование #${editingId}` : "Создание промокода"}
                        </div>
                        {detailsLoading ? <div className="section-empty">Загрузка деталей...</div> : null}
                        {!detailsLoading ? (
                            <form className="section-form" onSubmit={handleSave}>
                                <div className="section-inline">
                                    <div className="section-field">
                                        <div className="section-label">Код</div>
                                        <input
                                            className="section-input"
                                            value={form.title}
                                            onChange={(event) => setForm((current) => ({ ...current, title: event.target.value.toUpperCase() }))}
                                            placeholder="SKIDKA10"
                                            disabled={editingId !== null && !canEdit}
                                        />
                                    </div>
                                    <div className="section-field">
                                        <div className="section-label">Тип скидки</div>
                                        <select
                                            className="section-select"
                                            value={form.discountType}
                                            onChange={(event) => setForm((current) => ({
                                                ...current,
                                                discountType: event.target.value as PromoForm["discountType"],
                                            }))}
                                            disabled={editingId !== null && !canEdit}
                                        >
                                            <option value="percentage">Проценты</option>
                                            <option value="rub">Рубли</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="section-inline">
                                    <div className="section-field">
                                        <div className="section-label">Размер скидки</div>
                                        <input
                                            className="section-input"
                                            type="number"
                                            min="1"
                                            value={form.discount}
                                            onChange={(event) => setForm((current) => ({ ...current, discount: event.target.value }))}
                                            disabled={editingId !== null && !canEdit}
                                        />
                                    </div>
                                    <div className="section-field">
                                        <div className="section-label">С</div>
                                        <input
                                            className="section-input"
                                            type="date"
                                            value={form.startDate}
                                            onChange={(event) => setForm((current) => ({ ...current, startDate: event.target.value }))}
                                            disabled={editingId !== null && !canEdit}
                                        />
                                    </div>
                                    <div className="section-field">
                                        <div className="section-label">По</div>
                                        <input
                                            className="section-input"
                                            type="date"
                                            value={form.endDate}
                                            onChange={(event) => setForm((current) => ({ ...current, endDate: event.target.value }))}
                                            disabled={editingId !== null && !canEdit}
                                        />
                                    </div>
                                </div>

                                <div className="section-checkbox-list">
                                    <label className="section-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={form.registration}
                                            onChange={(event) => setForm((current) => ({ ...current, registration: event.target.checked }))}
                                            disabled={editingId !== null && !canEdit}
                                        />
                                        <span>Только с регистрацией</span>
                                    </label>
                                    <label className="section-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={form.isReusable}
                                            onChange={(event) => setForm((current) => ({ ...current, isReusable: event.target.checked }))}
                                            disabled={editingId !== null && !canEdit}
                                        />
                                        <span>Многоразовый промокод</span>
                                    </label>
                                    <label className="section-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={form.forNewUsersOnly}
                                            onChange={(event) => setForm((current) => ({ ...current, forNewUsersOnly: event.target.checked }))}
                                            disabled={editingId !== null && !canEdit}
                                        />
                                        <span>Только для новых клиентов</span>
                                    </label>
                                </div>

                                <div className="section-field">
                                    <div className="section-label">Услуги</div>
                                    {serviceGroups.length === 0 ? (
                                        <div className="section-empty">Список услуг пока недоступен</div>
                                    ) : (
                                        <div className="section-stack">
                                            {serviceGroups.map(([groupTitle, services]) => (
                                                <div key={groupTitle} className="section-card section-card-nested">
                                                    <div className="section-label">{groupTitle}</div>
                                                    <div className="section-checkbox-list">
                                                        {services.map((service) => (
                                                            <label className="section-checkbox" key={service.id}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={form.selectedServices.includes(service.id)}
                                                                    onChange={() => toggleService(service.id)}
                                                                    disabled={editingId !== null && !canEdit}
                                                                />
                                                                <span>{service.title}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="section-actions">
                                    <button className="section-btn" type="submit" disabled={saving || (editingId !== null && !canEdit)}>
                                        {saving ? "Сохраняем..." : editingId ? "Сохранить" : "Создать"}
                                    </button>
                                    {editingId && canEdit ? (
                                        <button className="section-btn-danger" type="button" disabled={saving} onClick={handleDelete}>
                                            Удалить
                                        </button>
                                    ) : null}
                                </div>
                                {editingId && !canEdit ? (
                                    <div className="section-helper">Глобальные промокоды доступны только для просмотра.</div>
                                ) : null}
                            </form>
                        ) : null}
                    </div>

                </>
            ) : null}
        </div>
    );
};
